import React from 'react'
import Icon from '@mdi/react'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'

const LONG_PRESS_MS = 420

/**
 * Botón de categoría Oviyam: click = default; long-press / contextmenu = subitems.
 * Hint de 3 puntos = tiene submenu (mismo gesto que VoiceNote / DSA).
 *
 * @param {{
 *   title?: string,
 *   path: string,
 *   active?: boolean,
 *   disabled?: boolean,
 *   items: Array<{
 *     id: string,
 *     label: string,
 *     path: string,
 *     disabled?: boolean,
 *     active?: boolean,
 *     onClick: () => void,
 *   }>,
 *   onPrimary: () => void,
 * }} props
 */
function OviyamNgFlyoutTool({
  title,
  path,
  active = false,
  disabled = false,
  items = [],
  onPrimary,
}) {
  const btnRef = React.useRef(null)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const longPressTimer = React.useRef(null)
  const longPressFired = React.useRef(false)
  const ignoreClickUntil = React.useRef(0)
  const pointerId = React.useRef(null)

  const hasMenu = Array.isArray(items) && items.length > 0
  const menuOpen = !!anchorEl

  const clearLongPress = React.useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  React.useEffect(() => () => clearLongPress(), [clearLongPress])

  const openMenu = React.useCallback(() => {
    if (!hasMenu || disabled) return
    longPressFired.current = true
    ignoreClickUntil.current = Date.now() + 500
    setAnchorEl(btnRef.current)
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try {
        navigator.vibrate(12)
      } catch (err) {
        /* ignore */
      }
    }
  }, [hasMenu, disabled])

  const closeMenu = () => setAnchorEl(null)

  const handlePointerDown = (e) => {
    if (disabled || !hasMenu) return
    if (e.button != null && e.button !== 0) return
    longPressFired.current = false
    pointerId.current = e.pointerId
    clearLongPress()
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null
      openMenu()
    }, LONG_PRESS_MS)
  }

  const handlePointerUp = (e) => {
    clearLongPress()
    if (pointerId.current != null && e.pointerId !== pointerId.current) return
    pointerId.current = null
  }

  const handlePointerCancel = () => {
    clearLongPress()
    pointerId.current = null
  }

  const handleClick = (e) => {
    if (disabled) return
    if (longPressFired.current || Date.now() < ignoreClickUntil.current) {
      e.preventDefault()
      e.stopPropagation()
      longPressFired.current = false
      return
    }
    if (onPrimary) onPrimary()
  }

  const handleContextMenu = (e) => {
    if (!hasMenu || disabled) return
    e.preventDefault()
    e.stopPropagation()
    openMenu()
  }

  const tip = hasMenu
    ? `${title || ''} · mantener / clic derecho = más`.trim()
    : title

  const cls = [
    'udv-oviyamng-tool',
    'udv-oviyamng-flyout',
    active ? 'is-active' : '',
    menuOpen ? 'is-menu-open' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={cls}
        title={tip}
        aria-label={tip}
        aria-haspopup={hasMenu ? 'menu' : undefined}
        aria-expanded={hasMenu ? menuOpen : undefined}
        disabled={disabled}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerCancel}
        onPointerCancel={handlePointerCancel}
        onContextMenu={handleContextMenu}
      >
        <Icon path={path} size={0.78} color="currentColor" />
        {hasMenu ? (
          <span className="udv-oviyamng-flyout-hint" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        ) : null}
      </button>
      {hasMenu ? (
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={closeMenu}
          keepMounted
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          PaperProps={{ className: 'udv-oviyamng-menu-paper' }}
          MenuListProps={{ dense: true, 'aria-label': title }}
        >
          {items.map((item) => (
            <MenuItem
              key={item.id}
              className={`udv-oviyamng-menu-item${item.active ? ' is-active' : ''}`}
              disabled={!!item.disabled}
              onClick={() => {
                closeMenu()
                if (item.onClick) item.onClick()
              }}
            >
              <ListItemIcon style={{ minWidth: 28, color: 'inherit' }}>
                <Icon path={item.path} size={0.72} color="currentColor" />
              </ListItemIcon>
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </>
  )
}

export default OviyamNgFlyoutTool
